import { isNaN, mean, sum } from '@soltalabs/ramda-extra'
import CONSTANTS from '@roadscience/constants'
import { groupWith, path } from 'ramda'

import { COLORS } from '../constants/colors'
import { getRiskContributions } from './riskTerms'

const { ALGORITHM_INPUTS } = CONSTANTS

// Mirrors THRESHOLDS in packages/constants/src/index.js. Kept local because the
// web portal resolves @roadscience/constants to vendor/, whose stub thresholds
// use a different shape and placeholder values.
const THRESHOLDS = {
  DEFAULT: {
    [ALGORITHM_INPUTS.TEMPERATURE_FEEL_LIKE]: { MIN: 15 },
    [ALGORITHM_INPUTS.RELATIVE_HUMIDITY]: { MAX: 80 },
    [ALGORITHM_INPUTS.QUANTITATIVE_RAINFALL]: { MAX: 0.1 },
    [ALGORITHM_INPUTS.WIND_SPEED]: { MAX: 8 },
    [ALGORITHM_INPUTS.PRECIP_CHANCE]: { MAX: 15 },
  },
  RS1: {
    [ALGORITHM_INPUTS.TEMPERATURE_FEEL_LIKE]: { MIN: 15 },
    [ALGORITHM_INPUTS.RELATIVE_HUMIDITY]: { MAX: 80 },
    [ALGORITHM_INPUTS.QUANTITATIVE_RAINFALL]: { MAX: 0.1 },
  },
  ASPHALT: {
    [ALGORITHM_INPUTS.TEMPERATURE_FEEL_LIKE]: { MIN: 15 },
    [ALGORITHM_INPUTS.QUANTITATIVE_RAINFALL]: { MAX: 0.1 },
  },
  SYD_ASPHALT: {
    [ALGORITHM_INPUTS.TEMPERATURE_FEEL_LIKE]: { MIN: 15 },
    [ALGORITHM_INPUTS.WIND_SPEED]: { MAX: 8 },
    [ALGORITHM_INPUTS.PRECIP_CHANCE]: { MAX: 15 },
  },
  REJUV: {
    [ALGORITHM_INPUTS.TEMPERATURE_FEEL_LIKE]: { MIN: 15 },
    [ALGORITHM_INPUTS.RELATIVE_HUMIDITY]: { MAX: 80 },
    [ALGORITHM_INPUTS.QUANTITATIVE_RAINFALL]: { MAX: 0.1 },
  },
}

const INPUT_AGGREGATOR = {
  [ALGORITHM_INPUTS.TEMPERATURE_FEEL_LIKE]: mean,
  [ALGORITHM_INPUTS.RELATIVE_HUMIDITY]: mean,
  [ALGORITHM_INPUTS.QUANTITATIVE_RAINFALL]: sum,
  [ALGORITHM_INPUTS.PRECIP_CHANCE]: mean,
  [ALGORITHM_INPUTS.WIND_SPEED]: mean,
}

// Road Science rule: a total risk of 1 or more (any single breached limit) is Red.
function getColorFromAssessmentScore(value) {
  if (value <= 0) {
    return COLORS.ASSESSMENT.GREEN
  }
  if (value < 1) {
    return COLORS.ASSESSMENT.YELLOW
  }

  return COLORS.ASSESSMENT.RED
}

function getAssessmentScoreCondition(assessment) {
  const { score } = assessment

  if (score <= 0) {
    return COLORS.ASSESSMENT.GREEN
  }
  if (score < 1) {
    return COLORS.ASSESSMENT.YELLOW
  }

  return COLORS.ASSESSMENT.RED
}

function groupAssessmentsByCondition(assessments) {
  return groupWith(
    (a, b) => getAssessmentScoreCondition(a) === getAssessmentScoreCondition(b),
    assessments
  )
}

function summarizeAssessmentGroup(assessmentGroup) {
  const { temperatureFeelLike = [], relativeHumidity = [], quantitativeRainfall = [] } = path(
    [0, 'algorithmInputs'],
    assessmentGroup
  ) ?? {}

  return {
    color: getAssessmentScoreCondition(assessmentGroup[0]),
    meanTemp: mean(temperatureFeelLike),
    meanHumidity: mean(relativeHumidity),
    meanRainfall: mean(quantitativeRainfall),
    startTime: assessmentGroup[0].assessmentValidityPeriodStart,
    endTime: assessmentGroup[assessmentGroup.length - 1].assessmentValidityPeriodEnd,
    length: assessmentGroup.length,
    assessments: assessmentGroup,
  }
}

function assessAlgorithmInputs(algorithm, algorithmInputs) {
  const scores = {}

  for (const [input, value] of Object.entries(algorithmInputs)) {
    const aggregator = INPUT_AGGREGATOR[input]

    if (!aggregator || isNaN(value)) {
      scores[input] = 0
      continue
    }

    const aggregatedValue = aggregator(value)
    scores[input] = calculateScore(algorithm, input, aggregatedValue)
  }

  return scores
}

function assessGroupAlgorithmInputs(algorithm, assessments) {
  const scores = {}

  for (const { algorithmInputs = {} } of assessments) {
    for (const [input, score] of Object.entries(
      assessAlgorithmInputs(algorithm, algorithmInputs)
    )) {
      scores[input] = Math.max(scores[input] ?? 0, score)
    }
  }

  return scores
}

// A reason only applies to hours that actually carry risk, and only when that
// variable contributes to the score, so a label can never contradict the colour.
// Falls back to the flat thresholds for algorithms we cannot re-compute.
const REASON_TOLERANCE = 1e-9

function assessGroupReasons(product, assessments) {
  const algorithm = product?.applicableAlgorithm?.split(':')[0].toUpperCase()
  const reasons = {}

  for (const { score, algorithmInputs = {} } of assessments) {
    if (!(score > 0)) {
      continue
    }

    const contributions = getRiskContributions(product, algorithmInputs)
    const hourReasons = contributions
      ? Object.fromEntries(
          Object.entries(contributions).map(([input, value]) => [
            input,
            value > REASON_TOLERANCE ? 1 : 0,
          ])
        )
      : assessAlgorithmInputs(algorithm, algorithmInputs)

    for (const [input, reason] of Object.entries(hourReasons)) {
      reasons[input] = Math.max(reasons[input] ?? 0, reason)
    }
  }

  return reasons
}

function calculateScore(algorithm, input, aggregatedValue) {
  const algorithmThresholds = THRESHOLDS[algorithm] ?? THRESHOLDS.DEFAULT
  const inputThreshold = algorithmThresholds[input]

  if (!inputThreshold) {
    return 0
  }

  // 1 = this input is outside its safe range and is a reason for the risk
  if (inputThreshold.MIN != null && aggregatedValue < inputThreshold.MIN) {
    return 1
  }
  if (inputThreshold.MAX != null && aggregatedValue > inputThreshold.MAX) {
    return 1
  }

  return 0
}

export {
  assessAlgorithmInputs,
  assessGroupAlgorithmInputs,
  assessGroupReasons,
  getColorFromAssessmentScore,
  getAssessmentScoreCondition,
  groupAssessmentsByCondition,
  summarizeAssessmentGroup,
}
